import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { AuthContext } from "../../components/AuthContext";
import MatematronModal from "../../components/ui/MatematronModal";
import {
  getWhiteboardSocket,
  whiteboardCreateRoom,
  whiteboardJoinRoom,
  whiteboardPing,
  whiteboardSetVisibility,
} from "../ws/whiteboardClient";
import {
  CopyCodeIcon,
  LinkChainIcon,
  PenIcon,
  TaskIcon,
  TrashCanIcon,
} from "../components/WhiteboardControlPrimitives";

const DEFAULT_SHARED_BOARD_TITLE_BASE = "Wspólna tablica";
const DEFAULT_PERSONAL_BOARD_TITLE_BASE = "Własna tablica";
const FIXED_MAX_PARTICIPANTS = 4;
const VISIBILITY_SHARED = "shared";
const VISIBILITY_PERSONAL = "personal";
const SECTION_EXPANDED_STORAGE_KEY = "whiteboard.lobby.sections.v1";
const DEFAULT_EXPANDED_SECTIONS = {
  groups: true,
  ownShared: true,
  ownPersonal: true,
  sharedWithMe: true,
};
const LOBBY_BATCH_SIZE = 5;
const BOARD_FETCH_BATCH_SIZE = LOBBY_BATCH_SIZE;
const BOARD_RENDER_BATCH_SIZE = LOBBY_BATCH_SIZE;
const GROUP_BOARD_INITIAL_BATCH_SIZE = LOBBY_BATCH_SIZE;
const GROUP_BOARD_FETCH_BATCH_SIZE = LOBBY_BATCH_SIZE;
const SEARCH_MIN_QUERY_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 500;
const SEARCH_PAGE_SIZE = 30;
let whiteboardLobbyBoardsCache = [];
let whiteboardLobbyBoardsTotalCache = 0;
let whiteboardLobbyBoardCountsCache = {
  ownShared: 0,
  ownPersonal: 0,
  sharedWithMe: 0,
  total: 0,
};

const wait = (delayMs) => new Promise((resolve) => window.setTimeout(resolve, delayMs));

async function fetchJsonWithRetry(fetcher, url, attempts = 4) {
  let lastError = null;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      const res = await fetcher(url);
      const data = await res.json().catch(() => null);
      if (res.ok) return { res, data };
      const error = new Error(`request_failed_${res.status}`);
      error.status = res.status;
      lastError = error;
      if (res.status < 500 && res.status !== 429) throw error;
    } catch (error) {
      lastError = error;
      if (Number(error?.status || 0) > 0 && Number(error.status) < 500 && Number(error.status) !== 429) {
        throw error;
      }
    }
    if (attempt < attempts - 1) {
      await wait(500 * (2 ** attempt));
    }
  }
  throw lastError || new Error("request_failed");
}

function normalizeVisibilityType(value) {
  return String(value || "").trim().toLowerCase() === VISIBILITY_PERSONAL
    ? VISIBILITY_PERSONAL
    : VISIBILITY_SHARED;
}

function getDefaultTitleBase(visibilityType) {
  return normalizeVisibilityType(visibilityType) === VISIBILITY_PERSONAL
    ? DEFAULT_PERSONAL_BOARD_TITLE_BASE
    : DEFAULT_SHARED_BOARD_TITLE_BASE;
}

function getVisibilityBadgeLabel(visibilityType, isOwner) {
  const type = normalizeVisibilityType(visibilityType);
  if (!isOwner) return "Udostępniona";
  return type === VISIBILITY_PERSONAL ? "Własna" : "Współdzielona";
}

function fmtDate(value) {
  if (!value) return "brak daty";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "brak daty";
  return d.toLocaleString();
}

function computeNextDefaultBoardTitle(boards, ownerUserId, visibilityType = VISIBILITY_SHARED) {
  const ownerId = String(ownerUserId || "");
  const base = getDefaultTitleBase(visibilityType);
  const ownBoards = (Array.isArray(boards) ? boards : []).filter(
    (board) => String(board?.ownerUserId || "") === ownerId
  );
  if (!ownBoards.length) return base;

  const escapedBase = base.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(`^${escapedBase}(?:\\s+(\\d+))?$`, "i");
  let maxIndex = 0;
  for (const board of ownBoards) {
    const title = String(board?.title || "").trim();
    const match = title.match(re);
    if (!match) continue;
    const parsed = Number(match[1]);
    const current = Number.isFinite(parsed) && parsed >= 2 ? parsed : 1;
    if (current > maxIndex) maxIndex = current;
  }

  if (maxIndex <= 0) return base;
  return `${base} ${maxIndex + 1}`;
}

function computeNextGroupedBoardTitle(groupName, boardsInGroup) {
  const base = String(groupName || "").trim() || DEFAULT_SHARED_BOARD_TITLE_BASE;
  const escaped = base.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(`^${escaped}(?:\\s+(\\d+))?$`, "i");
  let maxIndex = 0;
  for (const board of Array.isArray(boardsInGroup) ? boardsInGroup : []) {
    const title = String(board?.title || "").trim();
    const match = title.match(re);
    if (!match) continue;
    const parsed = Number(match[1]);
    const current = Number.isFinite(parsed) && parsed >= 2 ? parsed : 1;
    if (current > maxIndex) maxIndex = current;
  }
  if (maxIndex <= 0) return base;
  return `${base} ${maxIndex + 1}`;
}

const iconButtonStyle = {
  minWidth: 42,
  height: 42,
  padding: "0 10px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 16,
  lineHeight: 1,
};

function ToggleArrowIcon({ expanded }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d={expanded ? "M6 14.5 12 8l6 6.5H6Z" : "M6 9.5 12 16l6-6.5H6Z"}
        fill="currentColor"
      />
    </svg>
  );
}

function CollapsibleSection({
  title,
  countLabel,
  expanded,
  onToggle,
  children,
  emptyText = "Brak elementów w tej sekcji.",
}) {
  return (
    <div style={{ marginTop: 12 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          marginBottom: expanded ? 8 : 0,
        }}
      >
        <div style={{ minWidth: 0, display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
          <div style={{ fontWeight: 800 }}>{title}</div>
          {countLabel ? <div style={{ fontSize: 12, opacity: 0.72 }}>({countLabel})</div> : null}
        </div>
        <button
          className="btn btn-secondary"
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          title={expanded ? "Zwiń sekcję" : "Rozwiń sekcję"}
          aria-label={expanded ? `Zwiń sekcję ${title}` : `Rozwiń sekcję ${title}`}
          style={iconButtonStyle}
        >
          <ToggleArrowIcon expanded={expanded} />
        </button>
      </div>
      {expanded ? children || <div style={{ fontSize: 14, opacity: 0.78 }}>{emptyText}</div> : null}
    </div>
  );
}

function BoardListSection({
  title,
  items,
  hideHeader = false,
  currentUserId,
  isAdmin,
  onOpen,
  openingCode,
  onDelete,
  onMoveGroup,
  onCreateGroup,
  editingCode,
  editingTitle,
  renameSaving,
  onStartRename,
  onRenameChange,
  onRenameCancel,
  onRenameSubmit,
  onChangeVisibility,
  visibilitySavingCode,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  renderBatchSize = BOARD_RENDER_BATCH_SIZE,
}) {
  const [expandedParticipants, setExpandedParticipants] = useState({});
  const [visibleCount, setVisibleCount] = useState(renderBatchSize);

  useEffect(() => {
    setVisibleCount(renderBatchSize);
  }, [renderBatchSize, title]);

  const visibleItems = items.slice(0, visibleCount);

  function toggleParticipants(boardCode) {
    const codeKey = String(boardCode || "").trim().toUpperCase();
    if (!codeKey) return;
    setExpandedParticipants((prev) => ({ ...prev, [codeKey]: !prev[codeKey] }));
  }

  return (
    <div style={{ marginTop: 12 }}>
      {!hideHeader ? <div style={{ fontWeight: 800, marginBottom: 8 }}>{title}</div> : null}
      {items.length === 0 ? (
        <div style={{ fontSize: 14, opacity: 0.78 }}>Brak tablic w tej sekcji.</div>
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          {visibleItems.map((board) => {
            const isOwner = String(board.ownerUserId || "") === String(currentUserId || "");
            const isReadOnly = board?.readOnly === true || Boolean(board?.readOnlyAt);
            const canDelete = isOwner || isAdmin;
            const canRename = !isReadOnly && Boolean(currentUserId);
            const canChangeVisibility = !isReadOnly && (isOwner || isAdmin);
            const isEditing = editingCode === board.code;
            const visibilityType = normalizeVisibilityType(board?.visibilityType);
            const isShareEnabled = board?.isShareEnabled !== false;
            const canCopyJoin = isReadOnly || (visibilityType === VISIBILITY_SHARED && isShareEnabled);
            const canGroup = !isReadOnly && Boolean(currentUserId);
            const participants = Array.isArray(board?.participants) ? board.participants : [];
            const normalizedCode = String(board?.code || "").trim().toUpperCase();
            const participantsExpanded = !!expandedParticipants[normalizedCode];

            return (
              <div
                key={board.code}
                className="themed-sub-island themed-island--secondary"
                style={{
                  padding: "12px 14px",
                  borderRadius: 14,
                  border: "1px solid rgba(245,158,11,0.45)",
                  display: "flex",
                  gap: 10,
                  flexWrap: "wrap",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div style={{ minWidth: 0, flex: "1 1 320px" }}>
                  {isEditing ? (
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <input
                        className="input-secondary"
                        value={editingTitle}
                        autoFocus
                        disabled={renameSaving}
                        onChange={(e) => onRenameChange?.(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            onRenameSubmit?.(board);
                          }
                          if (e.key === "Escape") {
                            e.preventDefault();
                            onRenameCancel?.();
                          }
                        }}
                        onBlur={() => {
                          if (!renameSaving) onRenameCancel?.();
                        }}
                        style={{ minWidth: 240, maxWidth: "100%" }}
                      />
                      <button
                        className="btn btn-secondary"
                        type="button"
                        disabled={renameSaving}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => onRenameSubmit?.(board)}
                        title="Zapisz nową nazwę"
                        aria-label="Zapisz nową nazwę"
                        style={iconButtonStyle}
                      >
                        ✓
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <div style={{ fontWeight: 800 }}>{board.title || `Tablica ${board.code}`}</div>
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 800,
                          border: "1px solid rgba(245,158,11,0.55)",
                          borderRadius: 999,
                          padding: "2px 8px",
                          opacity: 0.9,
                        }}
                      >
                        {getVisibilityBadgeLabel(visibilityType, isOwner)}
                      </span>
                      {isReadOnly ? (
                        <span
                          title={board?.readOnlyAt ? `Zarchiwizowana: ${fmtDate(board.readOnlyAt)}` : ""}
                          style={{
                            fontSize: 11,
                            fontWeight: 800,
                            border: "1px solid rgba(148,163,184,0.65)",
                            borderRadius: 999,
                            padding: "2px 8px",
                            opacity: 0.92,
                          }}
                        >
                          Tylko do odczytu
                        </span>
                      ) : null}
                      {canRename ? (
                        <button
                          className="btn btn-secondary"
                          type="button"
                          onClick={() => onStartRename?.(board)}
                          title="Zmień nazwę"
                          aria-label="Zmień nazwę"
                          style={iconButtonStyle}
                        >
                          <PenIcon />
                        </button>
                      ) : null}
                      {canChangeVisibility && typeof onChangeVisibility === "function" ? (
                        <button
                          className="btn btn-secondary"
                          type="button"
                          disabled={visibilitySavingCode === normalizedCode}
                          onClick={() => onChangeVisibility(board)}
                          title={visibilityType === VISIBILITY_PERSONAL ? "Zmień na współdzieloną" : "Zmień na własną (prywatną)"}
                          style={{ fontSize: 11, padding: "4px 9px", minHeight: 28 }}
                        >
                          {visibilitySavingCode === normalizedCode
                            ? "Zmieniam..."
                            : visibilityType === VISIBILITY_PERSONAL
                              ? "Ustaw współdzieloną"
                              : "Ustaw własną"}
                        </button>
                      ) : null}
                    </div>
                  )}

                  <div style={{ fontSize: 12, opacity: 0.78, marginTop: 4 }}>
                    Kod: <b>{board.code}</b> · Utworzona: <b>{fmtDate(board.createdAt)}</b> · Zmieniona:{" "}
                    <b>{fmtDate(board.updatedAt)}</b>
                  </div>
                  <div style={{ fontSize: 12, opacity: 0.74, marginTop: 4 }}>
                    {isOwner ? "Właściciel" : "Współdzielona ze mną"}
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <button
                      className="btn btn-secondary"
                      type="button"
                      onClick={() => toggleParticipants(board.code)}
                      style={{ fontSize: 12, padding: "6px 10px", minHeight: 30 }}
                    >
                      Uczestnicy: {participants.length} {participantsExpanded ? "▴" : "▾"}
                    </button>
                    {participantsExpanded ? (
                      <div
                        style={{
                          marginTop: 8,
                          fontSize: 12,
                          opacity: 0.9,
                          display: "grid",
                          gap: 4,
                        }}
                      >
                        {participants.length ? (
                          participants.map((p) => {
                            const participantId = String(p?.userId || "");
                            const label = String(p?.name || p?.userId || "uczestnik");
                            const roleLabel = String(p?.role || "").trim();
                            const ownerMark =
                              participantId && participantId === String(board?.ownerUserId || "")
                                ? " (właściciel)"
                                : roleLabel
                                ? ` (${roleLabel})`
                                : "";
                            return <div key={`${normalizedCode}:${participantId || label}`}>{label}{ownerMark}</div>;
                          })
                        ) : (
                          <div>Brak uczestników.</div>
                        )}
                      </div>
                    ) : null}
                  </div>
                </div>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {canGroup && typeof onMoveGroup === "function" ? (
                    <button
                      className="btn btn-secondary"
                      type="button"
                      onClick={() => onMoveGroup(board)}
                      title="Przenieś do innej grupy"
                      aria-label="Przenieś do innej grupy"
                      style={iconButtonStyle}
                    >
                      ⇄
                    </button>
                  ) : null}
                  {!board?.groupId && canGroup && typeof onCreateGroup === "function" ? (
                    <button
                      className="btn btn-secondary"
                      type="button"
                      onClick={() => onCreateGroup(board)}
                      title="Utwórz grupę z tej tablicy"
                      aria-label="Utwórz grupę z tej tablicy"
                      style={iconButtonStyle}
                    >
                      <TaskIcon />
                    </button>
                  ) : null}
                  {canCopyJoin ? (
                    <>
                      <button
                        className="btn btn-secondary"
                        type="button"
                        onClick={() =>
                          navigator.clipboard?.writeText(
                            `${window.location.origin}/tablica/${String(board.code || "").trim().toUpperCase()}`
                          )
                        }
                        title="Kopiuj link"
                        aria-label="Kopiuj link"
                        style={iconButtonStyle}
                      >
                        <LinkChainIcon />
                      </button>
                      <button
                        className="btn btn-secondary"
                        type="button"
                        onClick={() => navigator.clipboard?.writeText(board.code)}
                        title="Kopiuj kod"
                        aria-label="Kopiuj kod"
                        style={iconButtonStyle}
                      >
                        <CopyCodeIcon />
                      </button>
                    </>
                  ) : null}
                  {canDelete ? (
                    <button
                      className="btn btn-secondary"
                      type="button"
                      onClick={() => onDelete(board)}
                      title="Usuń"
                      aria-label="Usuń"
                      style={iconButtonStyle}
                    >
                      <TrashCanIcon />
                    </button>
                  ) : null}
                  <button
                    className="btn btn-primary"
                    type="button"
                    disabled={openingCode === normalizedCode}
                    aria-busy={openingCode === normalizedCode}
                    onClick={() => onOpen?.(board)}
                  >
                    {openingCode === normalizedCode ? (
                      <><span className="button-busy-spinner" aria-hidden="true" /> Otwieram...</>
                    ) : "Otwórz"}
                  </button>
                </div>
              </div>
            );
          })}
          {visibleCount < items.length ? (
            <button
              className="btn btn-secondary"
              type="button"
              onClick={() => setVisibleCount((count) => Math.min(items.length, count + renderBatchSize))}
              style={{ justifySelf: "center" }}
            >
              Pokaż kolejne ({Math.min(renderBatchSize, items.length - visibleCount)})
            </button>
          ) : null}
          {visibleCount >= items.length && hasMore && typeof onLoadMore === "function" ? (
            <button
              className="btn btn-secondary"
              type="button"
              onClick={onLoadMore}
              disabled={loadingMore}
              aria-busy={loadingMore}
              style={{ justifySelf: "center" }}
            >
              {loadingMore ? "Wczytuję..." : "Pokaż kolejne"}
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}

function BoardsLoadingPlaceholder() {
  const cardStyle = {
    padding: "12px 14px",
    borderRadius: 14,
    border: "1px solid rgba(245,158,11,0.35)",
    background: "rgba(255,255,255,0.03)",
  };
  const line = (w) => ({
    height: 12,
    width: w,
    borderRadius: 8,
    background: "rgba(255,255,255,0.14)",
  });
  return (
    <div style={{ display: "grid", gap: 10 }}>
      {[0, 1, 2].map((idx) => (
        <div key={idx} style={cardStyle}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
            <div style={{ display: "grid", gap: 8, minWidth: 0, flex: "1 1 auto" }}>
              <div style={line(idx === 0 ? "38%" : idx === 1 ? "52%" : "44%")} />
              <div style={line(idx === 0 ? "62%" : idx === 1 ? "48%" : "58%")} />
              <div style={line("30%")} />
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <div style={{ width: 42, height: 42, borderRadius: 10, background: "rgba(255,255,255,0.14)" }} />
              <div style={{ width: 86, height: 42, borderRadius: 10, background: "rgba(245,158,11,0.32)" }} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function WhiteboardLobbyPage({ groupPage = false }) {
  const nav = useNavigate();
  const location = useLocation();
  const { groupId: routeGroupId = "" } = useParams();
  const focusedGroupId = useMemo(() => {
    if (groupPage) return String(routeGroupId || "").trim();
    try {
      return String(new URLSearchParams(location.search).get("group") || "").trim();
    } catch {
      return "";
    }
  }, [groupPage, location.search, routeGroupId]);
  const socket = useMemo(() => getWhiteboardSocket(), []);
  const { authorizedFetch } = useContext(AuthContext);

  const [ping, setPing] = useState(null);
  const [title, setTitle] = useState(DEFAULT_PERSONAL_BOARD_TITLE_BASE);
  const [titleTouched, setTitleTouched] = useState(false);
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [myBoards, setMyBoards] = useState(() => whiteboardLobbyBoardsCache);
  const [boardsLoading, setBoardsLoading] = useState(false);
  const [boardsLoadingMore, setBoardsLoadingMore] = useState(false);
  const [boardsTotal, setBoardsTotal] = useState(() => whiteboardLobbyBoardsTotalCache);
  const [boardsHasMore, setBoardsHasMore] = useState(false);
  const [boardCounts, setBoardCounts] = useState(() => whiteboardLobbyBoardCountsCache);
  const [boardsLoadError, setBoardsLoadError] = useState("");
  const [groupSummaries, setGroupSummaries] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupsTotal, setGroupsTotal] = useState(0);
  const [groupsHasMore, setGroupsHasMore] = useState(false);
  const [groupBoardsById, setGroupBoardsById] = useState({});
  const [groupBoardsLoading, setGroupBoardsLoading] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState({ boards: [], groups: [], total: 0, hasMore: false });
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchLoadingMore, setSearchLoadingMore] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [createBusy, setCreateBusy] = useState(false);
  const [joinBusy, setJoinBusy] = useState(false);
  const [openingCode, setOpeningCode] = useState("");
  const boardsLoadRequestRef = useRef(0);
  const groupsLoadBusyRef = useRef(false);
  const searchRequestRef = useRef(0);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editingCode, setEditingCode] = useState("");
  const [editingTitle, setEditingTitle] = useState("");
  const [renameSaving, setRenameSaving] = useState(false);
  const [visibilitySavingCode, setVisibilitySavingCode] = useState("");
  const [groupActionBusyKey, setGroupActionBusyKey] = useState("");
  const [expandedGroups, setExpandedGroups] = useState({});
  const [expandedSections, setExpandedSections] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(SECTION_EXPANDED_STORAGE_KEY) || "null");
      return { ...DEFAULT_EXPANDED_SECTIONS, ...(saved && typeof saved === "object" ? saved : {}) };
    } catch {
      return DEFAULT_EXPANDED_SECTIONS;
    }
  });
  const [createGroupTarget, setCreateGroupTarget] = useState(null);
  const [createGroupName, setCreateGroupName] = useState("");
  const [createBoardGroup, setCreateBoardGroup] = useState(null);
  const [createBoardTitle, setCreateBoardTitle] = useState("");
  const [createBoardVisibilityType, setCreateBoardVisibilityType] = useState(VISIBILITY_PERSONAL);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [moveTargetBoard, setMoveTargetBoard] = useState(null);
  const [moveGroupId, setMoveGroupId] = useState("");
  const [moveBoardBusy, setMoveBoardBusy] = useState(false);
  const [renameGroupTarget, setRenameGroupTarget] = useState(null);
  const [renameGroupName, setRenameGroupName] = useState("");
  const [deleteGroupTarget, setDeleteGroupTarget] = useState(null);

  useEffect(() => {
    let mounted = true;
    whiteboardPing().then((res) => {
      if (mounted) setPing(res);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const loadGroupSummaries = useCallback(async ({ append = false, offset = 0 } = {}) => {
    if (!authorizedFetch || groupsLoadBusyRef.current) return;
    const requestedOffset = append ? Math.max(0, Number(offset || 0)) : 0;
    groupsLoadBusyRef.current = true;
    setGroupsLoading(true);
    try {
      const res = await authorizedFetch(
        `/api/whiteboards/my/groups?limit=${LOBBY_BATCH_SIZE}&offset=${requestedOffset}`
      );
      const data = await res.json().catch(() => null);
      if (!res.ok) return;
      const nextGroups = Array.isArray(data?.groups) ? data.groups : [];
      setGroupSummaries((prev) => {
        const current = append ? prev : [];
        const byId = new Map(current.map((group) => [String(group?.id || ""), group]));
        nextGroups.forEach((group) => byId.set(String(group?.id || ""), group));
        return Array.from(byId.values());
      });
      setGroupsTotal(Math.max(Number(data?.total || 0), requestedOffset + nextGroups.length));
      setGroupsHasMore(Boolean(data?.hasMore));
    } catch {
      // Keep the last successfully loaded group list.
    } finally {
      groupsLoadBusyRef.current = false;
      setGroupsLoading(false);
    }
  }, [authorizedFetch]);

  const loadBoards = useCallback(async () => {
    if (!authorizedFetch) return;
    const requestId = boardsLoadRequestRef.current + 1;
    boardsLoadRequestRef.current = requestId;
    const hasCachedBoards = whiteboardLobbyBoardsCache.length > 0;
    setBoardsLoading(!hasCachedBoards);
    setBoardsLoadingMore(false);
    setBoardsLoadError("");
    try {
      const { data } = await fetchJsonWithRetry(
        authorizedFetch,
        `/api/whiteboards/my?scope=ungrouped&limit=${BOARD_FETCH_BATCH_SIZE}&offset=0`
      );
      if (boardsLoadRequestRef.current !== requestId) return;
      const firstItems = Array.isArray(data?.items) ? data.items : [];
      const total = Math.max(firstItems.length, Number(data?.total || 0));
      const nextCounts = {
        ownShared: Math.max(0, Number(data?.counts?.ownShared || 0)),
        ownPersonal: Math.max(0, Number(data?.counts?.ownPersonal || 0)),
        sharedWithMe: Math.max(0, Number(data?.counts?.sharedWithMe || 0)),
        total: Math.max(0, Number(data?.counts?.total ?? total)),
      };
      setMyBoards(firstItems);
      setBoardsTotal(total);
      setBoardCounts(nextCounts);
      setBoardsHasMore(Boolean(data?.hasMore));
      setBoardsLoading(false);
      whiteboardLobbyBoardsCache = firstItems;
      whiteboardLobbyBoardsTotalCache = total;
      whiteboardLobbyBoardCountsCache = nextCounts;
    } catch {
      if (boardsLoadRequestRef.current === requestId && !hasCachedBoards) {
        setBoardsLoadError("Nie udało się wczytać tablic. Dane nie zostały wyzerowane — spróbuj ponownie.");
      }
    } finally {
      if (boardsLoadRequestRef.current === requestId) {
        setBoardsLoading(false);
        setBoardsLoadingMore(false);
      }
    }
  }, [authorizedFetch]);

  const loadMoreBoards = useCallback(async () => {
    if (!authorizedFetch || boardsLoadingMore || !boardsHasMore) return;
    setBoardsLoadingMore(true);
    try {
      const res = await authorizedFetch(
        `/api/whiteboards/my?scope=ungrouped&limit=${BOARD_FETCH_BATCH_SIZE}&offset=${myBoards.length}`
      );
      const data = await res.json().catch(() => null);
      if (!res.ok) return;
      const nextItems = Array.isArray(data?.items) ? data.items : [];
      setMyBoards((prev) => {
        const byCode = new Map(prev.map((item) => [String(item?.code || ""), item]));
        nextItems.forEach((item) => byCode.set(String(item?.code || ""), item));
        return Array.from(byCode.values());
      });
      setBoardsTotal(Math.max(Number(data?.total || 0), myBoards.length + nextItems.length));
      setBoardsHasMore(Boolean(data?.hasMore));
    } finally {
      setBoardsLoadingMore(false);
    }
  }, [authorizedFetch, boardsHasMore, boardsLoadingMore, myBoards.length]);

  const loadGroupBoards = useCallback(async (groupId, { append = false } = {}) => {
    const gid = String(groupId || "").trim();
    if (!authorizedFetch || !gid) return;
    const current = groupBoardsById[gid];
    const offset = append ? Number(current?.items?.length || 0) : 0;
    const limit = append ? GROUP_BOARD_FETCH_BATCH_SIZE : GROUP_BOARD_INITIAL_BATCH_SIZE;
    setGroupBoardsLoading((prev) => ({ ...prev, [gid]: true }));
    try {
      const res = await authorizedFetch(
        `/api/whiteboards/my/groups/${encodeURIComponent(gid)}/boards?limit=${limit}&offset=${offset}`
      );
      const data = await res.json().catch(() => null);
      if (!res.ok) return;
      const nextItems = Array.isArray(data?.items) ? data.items : [];
      setGroupBoardsById((prev) => {
        const previousItems = append ? (prev[gid]?.items || []) : [];
        const byCode = new Map(previousItems.map((item) => [String(item?.code || ""), item]));
        nextItems.forEach((item) => byCode.set(String(item?.code || ""), item));
        return {
          ...prev,
          [gid]: {
            items: Array.from(byCode.values()),
            total: Math.max(Number(data?.total || 0), previousItems.length + nextItems.length),
            hasMore: Boolean(data?.hasMore),
            loaded: true,
            group: data?.group || prev[gid]?.group || null,
          },
        };
      });
    } finally {
      setGroupBoardsLoading((prev) => ({ ...prev, [gid]: false }));
    }
  }, [authorizedFetch, groupBoardsById]);

  useEffect(() => {
    let cancelled = false;
    const loadInitialLobby = async () => {
      await loadBoards();
      if (!cancelled) await loadGroupSummaries();
    };
    void loadInitialLobby();
    return () => {
      cancelled = true;
      boardsLoadRequestRef.current += 1;
    };
  }, [loadBoards, loadGroupSummaries]);

  useEffect(() => {
    if (groupPage) return undefined;
    const query = searchQuery.trim();
    const requestId = searchRequestRef.current + 1;
    searchRequestRef.current = requestId;
    if (query.length < SEARCH_MIN_QUERY_LENGTH) {
      setSearchResults({ boards: [], groups: [], total: 0, hasMore: false });
      setSearchError("");
      setSearchLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    setSearchLoading(true);
    setSearchError("");
    const timer = window.setTimeout(async () => {
      try {
        const res = await authorizedFetch(
          `/api/whiteboards/my/search?q=${encodeURIComponent(query)}&limit=${SEARCH_PAGE_SIZE}&offset=0`,
          { signal: controller.signal }
        );
        const data = await res.json().catch(() => null);
        if (!res.ok || !data?.ok) throw new Error(data?.error || "Nie udało się wyszukać tablic.");
        if (searchRequestRef.current === requestId) {
          setSearchResults({
            boards: Array.isArray(data?.boards) ? data.boards : [],
            groups: Array.isArray(data?.groups) ? data.groups : [],
            total: Math.max(0, Number(data?.total || 0)),
            hasMore: Boolean(data?.hasMore),
          });
        }
      } catch (error) {
        if (error?.name !== "AbortError" && searchRequestRef.current === requestId) {
          setSearchResults({ boards: [], groups: [], total: 0, hasMore: false });
          setSearchError(error?.message || "Nie udało się wyszukać tablic.");
        }
      } finally {
        if (searchRequestRef.current === requestId) setSearchLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [authorizedFetch, groupPage, searchQuery]);

  const loadMoreSearchResults = useCallback(async () => {
    const query = searchQuery.trim();
    if (!authorizedFetch || searchLoadingMore || query.length < SEARCH_MIN_QUERY_LENGTH || !searchResults.hasMore) return;
    const offset = searchResults.boards.length;
    const requestId = searchRequestRef.current;
    setSearchLoadingMore(true);
    try {
      const res = await authorizedFetch(
        `/api/whiteboards/my/search?q=${encodeURIComponent(query)}&limit=${SEARCH_PAGE_SIZE}&offset=${offset}`
      );
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.ok || searchRequestRef.current !== requestId) return;
      setSearchResults((prev) => {
        const byCode = new Map(prev.boards.map((board) => [String(board?.code || ""), board]));
        (Array.isArray(data?.boards) ? data.boards : []).forEach((board) => byCode.set(String(board?.code || ""), board));
        const groupById = new Map(prev.groups.map((group) => [String(group?.id || ""), group]));
        (Array.isArray(data?.groups) ? data.groups : []).forEach((group) => groupById.set(String(group?.id || ""), group));
        return {
          boards: Array.from(byCode.values()),
          groups: Array.from(groupById.values()),
          total: Math.max(Number(prev.total || 0), Number(data?.total || 0)),
          hasMore: Boolean(data?.hasMore),
        };
      });
    } finally {
      setSearchLoadingMore(false);
    }
  }, [authorizedFetch, searchLoadingMore, searchQuery, searchResults.boards.length, searchResults.hasMore]);

  useEffect(() => {
    whiteboardLobbyBoardsCache = myBoards;
    whiteboardLobbyBoardsTotalCache = boardsTotal;
  }, [boardsTotal, myBoards]);

  useEffect(() => {
    try {
      localStorage.setItem(SECTION_EXPANDED_STORAGE_KEY, JSON.stringify(expandedSections));
    } catch {
      // Local UI preference only.
    }
  }, [expandedSections]);

  const currentUserId = String(ping?.user?._id || "");
  const isAdmin = String(ping?.user?.role || "").toLowerCase() === "admin";
  const canCreate = Boolean(currentUserId);

  const ownBoards = myBoards.filter((board) => String(board.ownerUserId || "") === currentUserId);
  const ownSharedBoards = ownBoards.filter((board) => normalizeVisibilityType(board?.visibilityType) === VISIBILITY_SHARED);
  const ownPersonalBoards = ownBoards.filter((board) => normalizeVisibilityType(board?.visibilityType) === VISIBILITY_PERSONAL);
  const sharedWithMeBoards = myBoards.filter((board) => String(board.ownerUserId || "") !== currentUserId);

  const allGroupedBuckets = useMemo(
    () => groupSummaries.map((group) => ({
      ...group,
      items: groupBoardsById[String(group?.id || "")]?.items || [],
    })),
    [groupBoardsById, groupSummaries]
  );
  const ownSharedUngroupedBoards = ownSharedBoards.filter((board) => !String(board?.groupId || "").trim());
  const ownPersonalUngroupedBoards = ownPersonalBoards.filter((board) => !String(board?.groupId || "").trim());
  const sharedWithMeUngroupedBoards = sharedWithMeBoards.filter((board) => !String(board?.groupId || "").trim());
  const hasSearchQuery = !groupPage && Boolean(searchQuery.trim());
  const searchQueryReady = searchQuery.trim().length >= SEARCH_MIN_QUERY_LENGTH;
  const nextDefaultTitle = useMemo(
    () => computeNextDefaultBoardTitle(myBoards, currentUserId, createBoardVisibilityType),
    [myBoards, currentUserId, createBoardVisibilityType]
  );

  useEffect(() => {
    if (titleTouched) return;
    setTitle(nextDefaultTitle);
  }, [nextDefaultTitle, titleTouched]);

  useEffect(() => {
    setExpandedGroups((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const group of allGroupedBuckets) {
        if (!Object.prototype.hasOwnProperty.call(next, group.id)) {
          next[group.id] = false;
          changed = true;
        }
      }
      for (const key of Object.keys(next)) {
        if (!allGroupedBuckets.some((group) => group.id === key)) {
          delete next[key];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [allGroupedBuckets]);

  useEffect(() => {
    if (!focusedGroupId) return;
    if (!groupPage && !allGroupedBuckets.some((group) => group.id === focusedGroupId)) return;
    setExpandedSections((prev) => ({ ...prev, groups: true }));
    setExpandedGroups((prev) => ({ ...prev, [focusedGroupId]: true }));
    if (!groupBoardsById[focusedGroupId]?.loaded) void loadGroupBoards(focusedGroupId);
    if (groupPage) return;
    const timer = window.setTimeout(() => {
      document.getElementById(`whiteboard-group-${focusedGroupId}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [allGroupedBuckets, focusedGroupId, groupBoardsById, groupPage, loadGroupBoards]);

  async function onCreate() {
    if (createBusy) return;
    setErr("");
    const normalizedTitle = String(title || "").trim();
    const visibilityType = normalizeVisibilityType(createBoardVisibilityType);
    const finalTitle =
      normalizedTitle || computeNextDefaultBoardTitle(myBoards, currentUserId, visibilityType);
    setCreateBusy(true);
    const res = await whiteboardCreateRoom({
      title: finalTitle,
      maxParticipants: FIXED_MAX_PARTICIPANTS,
      visibilityType,
      isShareEnabled: visibilityType === VISIBILITY_SHARED,
    }).finally(() => setCreateBusy(false));

    if (!res?.ok) {
      setErr(`Nie udało się utworzyć tablicy: ${res?.error || "unknown"}`);
      return;
    }

    setCreateModalOpen(false);
    setCreateBoardVisibilityType(VISIBILITY_PERSONAL);
    setTitleTouched(false);
    setTitle(computeNextDefaultBoardTitle(myBoards, currentUserId, VISIBILITY_PERSONAL));
    nav(`/tablica/${res.code}`);
  }

  async function onJoin() {
    if (joinBusy) return;
    setErr("");
    const cleanCode = String(code || "").trim().toUpperCase();
    if (!cleanCode) {
      setErr("Wpisz kod tablicy.");
      return;
    }

    setJoinBusy(true);
    const res = await whiteboardJoinRoom(cleanCode).finally(() => setJoinBusy(false));
    if (!res?.ok) {
      setErr(`Nie udało się dołączyć: ${res?.error || "unknown"}`);
      return;
    }

    nav(`/tablica/${cleanCode}`);
  }

  function openBoard(board) {
    const normalizedCode = String(board?.code || "").trim().toUpperCase();
    if (!normalizedCode || openingCode) return;
    setOpeningCode(normalizedCode);
    window.setTimeout(() => nav(`/tablica/${normalizedCode}`), 0);
  }

  async function confirmDeleteBoard() {
    if (!deleteTarget || !authorizedFetch) return;
    setErr("");
    try {
      const res = await authorizedFetch(`/api/whiteboards/${deleteTarget.code}`, {
        method: "DELETE",
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        setErr(`Nie udało się usunąć tablicy: ${data?.error || "unknown"}`);
        return;
      }
      setDeleteTarget(null);
      void loadBoards();
      void loadGroupSummaries();
      if (deleteTarget?.groupId) {
        setGroupBoardsById((prev) => {
          const gid = String(deleteTarget.groupId || "");
          const current = prev[gid];
          if (!current) return prev;
          return {
            ...prev,
            [gid]: {
              ...current,
              items: current.items.filter((item) => String(item?.code || "") !== String(deleteTarget.code || "")),
              total: Math.max(0, Number(current.total || 0) - 1),
            },
          };
        });
      }
    } catch {
      setErr("Nie udało się usunąć tablicy.");
    }
  }

  function startRenameBoard(board) {
    setEditingCode(String(board?.code || ""));
    setEditingTitle(String(board?.title || ""));
  }

  function cancelRenameBoard() {
    setEditingCode("");
    setEditingTitle("");
    setRenameSaving(false);
  }

  async function submitRenameBoard(board) {
    const codeToRename = String(board?.code || editingCode || "").trim().toUpperCase();
    const nextTitle = String(editingTitle || "").trim();
    if (!codeToRename) return;
    if (!nextTitle) {
      setErr("Nazwa tablicy nie może być pusta.");
      return;
    }
    if (!authorizedFetch) return;

    setRenameSaving(true);
    setErr("");
    try {
      const res = await authorizedFetch(`/api/whiteboards/${codeToRename}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: nextTitle }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        setErr(`Nie udało się zmienić nazwy tablicy: ${data?.error || "unknown"}`);
        setRenameSaving(false);
        return;
      }
      setMyBoards((prev) =>
        prev.map((item) => (String(item.code || "") === codeToRename ? { ...item, title: nextTitle } : item))
      );
      setGroupBoardsById((prev) => Object.fromEntries(
        Object.entries(prev).map(([groupId, state]) => [
          groupId,
          {
            ...state,
            items: (state?.items || []).map((item) => (
              String(item?.code || "") === codeToRename ? { ...item, title: nextTitle } : item
            )),
          },
        ])
      ));
      cancelRenameBoard();
    } catch {
      setErr("Nie udało się zmienić nazwy tablicy.");
      setRenameSaving(false);
    }
  }

  async function changeBoardVisibility(board) {
    const codeToChange = String(board?.code || "").trim().toUpperCase();
    if (!codeToChange || visibilitySavingCode) return;
    const nextVisibility = normalizeVisibilityType(board?.visibilityType) === VISIBILITY_PERSONAL
      ? VISIBILITY_SHARED
      : VISIBILITY_PERSONAL;
    setVisibilitySavingCode(codeToChange);
    setErr("");
    try {
      const result = await whiteboardSetVisibility(codeToChange, nextVisibility);
      if (!result?.ok) {
        setErr(`Nie udało się zmienić typu tablicy: ${result?.error || "unknown"}`);
        return;
      }
      const patch = {
        visibilityType: nextVisibility,
        isShareEnabled: nextVisibility === VISIBILITY_SHARED,
      };
      setMyBoards((prev) => prev.map((item) => (
        String(item?.code || "").trim().toUpperCase() === codeToChange ? { ...item, ...patch } : item
      )));
      setGroupBoardsById((prev) => Object.fromEntries(
        Object.entries(prev).map(([groupId, state]) => [
          groupId,
          {
            ...state,
            items: (state?.items || []).map((item) => (
              String(item?.code || "").trim().toUpperCase() === codeToChange ? { ...item, ...patch } : item
            )),
          },
        ])
      ));
      void loadGroupSummaries();
    } catch {
      setErr("Nie udało się zmienić typu tablicy.");
    } finally {
      setVisibilitySavingCode("");
    }
  }

  function toggleGroup(groupId) {
    const gid = String(groupId || "").trim();
    if (!gid) return;
    const willExpand = !expandedGroups[gid];
    setExpandedGroups((prev) => ({ ...prev, [gid]: willExpand }));
    if (willExpand && !groupBoardsById[gid]?.loaded && !groupBoardsLoading[gid]) {
      void loadGroupBoards(gid);
    }
  }

  function toggleSection(sectionId) {
    const key = String(sectionId || "").trim();
    if (!key) return;
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  function openCreateGroupModal(board) {
    if (!board?.code) return;
    setCreateGroupTarget(board);
    setCreateGroupName(String(board?.title || "").trim() || DEFAULT_SHARED_BOARD_TITLE_BASE);
  }

  async function createGroupFromBoard() {
    const board = createGroupTarget;
    if (!authorizedFetch || !board?.code) return;
    const codeToGroup = String(board.code || "").trim().toUpperCase();
    const busyKey = `create_group:${codeToGroup}`;
    setGroupActionBusyKey(busyKey);
    setErr("");
    try {
      const res = await authorizedFetch(`/api/whiteboards/${codeToGroup}/group`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name:
            String(createGroupName || "").trim() ||
            String(board?.title || "").trim() ||
            DEFAULT_SHARED_BOARD_TITLE_BASE,
        }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.ok) {
        setErr(`Nie udało się utworzyć grupy: ${data?.error || "unknown"}`);
        return;
      }
      void loadBoards();
      void loadGroupSummaries();
      if (data?.group?.groupId) {
        setExpandedGroups((prev) => ({ ...prev, [String(data.group.groupId)]: false }));
      }
      setCreateGroupTarget(null);
      setCreateGroupName("");
    } catch {
      setErr("Nie udało się utworzyć grupy.");
    } finally {
      setGroupActionBusyKey("");
    }
  }

  function openCreateBoardInGroupModal(group) {
    if (!group?.id) return;
    setCreateBoardGroup(group);
    setCreateBoardTitle(Number(group?.count || 0) > 0 ? `${group.name} ${Number(group.count) + 1}` : group.name);
    setCreateBoardVisibilityType(normalizeVisibilityType(group?.visibilityType));
  }

  async function createBoardInGroup() {
    const group = createBoardGroup;
    if (!group?.id) return;
    const groupId = String(group.id || "").trim();
    if (!groupId) return;
    const busyKey = `create_board:${groupId}`;
    setGroupActionBusyKey(busyKey);
    setErr("");
    try {
      const visibilityType = normalizeVisibilityType(createBoardVisibilityType);
      const createRes = await whiteboardCreateRoom({
        title: String(createBoardTitle || "").trim() || (Number(group?.count || 0) > 0
          ? `${group.name} ${Number(group.count) + 1}`
          : group.name),
        maxParticipants: FIXED_MAX_PARTICIPANTS,
        visibilityType,
        isShareEnabled: visibilityType === VISIBILITY_SHARED,
      });
      if (!createRes?.ok) {
        setErr(`Nie udało się utworzyć tablicy: ${createRes?.error || "unknown"}`);
        return;
      }

      if (!authorizedFetch) {
        nav(`/tablica/${createRes.code}`);
        return;
      }

      const assignRes = await authorizedFetch(`/api/whiteboards/${String(createRes.code || "").trim().toUpperCase()}/group`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          groupId,
          groupName: String(group.name || "").trim() || null,
        }),
      });
      const assignData = await assignRes.json().catch(() => null);
      if (!assignRes.ok || !assignData?.ok) {
        setErr(`Tablica została utworzona, ale nie udało się przypisać do grupy: ${assignData?.error || "unknown"}`);
        nav(`/tablica/${createRes.code}`);
        return;
      }

      nav(`/tablica/${createRes.code}`);
      setCreateBoardGroup(null);
      setCreateBoardTitle("");
      setCreateBoardVisibilityType(VISIBILITY_SHARED);
    } catch {
      setErr("Nie udało się utworzyć tablicy w grupie.");
    } finally {
      setGroupActionBusyKey("");
    }
  }

  function openMoveBoardModal(board) {
    if (!board?.code) return;
    setMoveTargetBoard(board);
    setMoveGroupId(String(board?.groupId || "").trim());
  }

  async function confirmMoveBoardToGroup() {
    const board = moveTargetBoard;
    if (!authorizedFetch || !board?.code) return;

    const codeToMove = String(board.code || "").trim().toUpperCase();
    const targetGroupId = String(moveGroupId || "").trim();
    const targetGroup = allGroupedBuckets.find((g) => String(g.id || "") === targetGroupId);
    const targetGroupName = targetGroupId ? String(targetGroup?.name || "").trim() || "Grupa tablic" : null;
    // A board added to a group inherits the type of the latest board in that group.

    setMoveBoardBusy(true);
    setErr("");
    try {
      const res = await authorizedFetch(`/api/whiteboards/${codeToMove}/group`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          groupId: targetGroupId || null,
          groupName: targetGroupName,
        }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.ok) {
        setErr(`Nie udało się przenieść tablicy: ${data?.error || "unknown"}`);
        return;
      }
      if (targetGroupId) {
        const inheritedVisibility = normalizeVisibilityType(targetGroup?.visibilityType);
        const visibilityResult = await whiteboardSetVisibility(codeToMove, inheritedVisibility);
        if (!visibilityResult?.ok) {
          setErr(`Tablica została dodana do grupy, ale nie udało się ustawić jej typu zgodnie z ostatnią tablicą: ${visibilityResult?.error || "unknown"}`);
        }
      }
      void loadBoards();
      void loadGroupSummaries();
      setGroupBoardsById({});
      setMoveTargetBoard(null);
      setMoveGroupId("");
    } catch {
      setErr("Nie udało się przenieść tablicy.");
    } finally {
      setMoveBoardBusy(false);
    }
  }

  function openRenameGroupModal(group) {
    if (!group?.id) return;
    setRenameGroupTarget(group);
    setRenameGroupName(String(group?.name || "").trim() || "Grupa tablic");
  }

  async function confirmRenameGroup() {
    const group = renameGroupTarget;
    if (!authorizedFetch || !group?.id) return;
    const groupId = String(group.id || "").trim();
    const groupName = String(renameGroupName || "").trim();
    if (!groupName) {
      setErr("Nazwa grupy nie może być pusta.");
      return;
    }
    const busyKey = `rename_group:${groupId}`;
    setGroupActionBusyKey(busyKey);
    setErr("");
    try {
      const res = await authorizedFetch(`/api/whiteboards/groups/${groupId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ groupName }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.ok) {
        setErr(`Nie udało się zmienić nazwy grupy: ${data?.error || "unknown"}`);
        return;
      }
      void loadBoards();
      void loadGroupSummaries();
      setRenameGroupTarget(null);
      setRenameGroupName("");
    } catch {
      setErr("Nie udało się zmienić nazwy grupy.");
    } finally {
      setGroupActionBusyKey("");
    }
  }

  function openDeleteGroupModal(group) {
    if (!group?.id) return;
    setDeleteGroupTarget(group);
  }

  async function confirmDeleteGroup() {
    const group = deleteGroupTarget;
    if (!authorizedFetch || !group?.id) return;
    const groupId = String(group.id || "").trim();
    const busyKey = `delete_group:${groupId}`;
    setGroupActionBusyKey(busyKey);
    setErr("");
    try {
      const res = await authorizedFetch(`/api/whiteboards/groups/${groupId}`, {
        method: "DELETE",
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.ok) {
        setErr(`Nie udało się usunąć grupy: ${data?.error || "unknown"}`);
        return;
      }
      void loadBoards();
      void loadGroupSummaries();
      setDeleteGroupTarget(null);
    } catch {
      setErr("Nie udało się usunąć grupy.");
    } finally {
      setGroupActionBusyKey("");
    }
  }

  function renderGroupCard(group) {
    const isExpanded = !!expandedGroups[group.id];
    const createBusy = groupActionBusyKey === `create_board:${group.id}`;
    const renameBusy = groupActionBusyKey === `rename_group:${group.id}`;
    const deleteBusy = groupActionBusyKey === `delete_group:${group.id}`;
    const anyBusy = createBusy || renameBusy || deleteBusy;

    return (
      <div
        key={group.id}
        id={`whiteboard-group-${group.id}`}
        className="themed-sub-island themed-island--secondary"
        style={{
          padding: "12px 14px",
          borderRadius: 14,
          border: focusedGroupId === group.id ? "2px solid var(--color-accent)" : "1px solid rgba(245,158,11,0.45)",
          boxShadow: focusedGroupId === group.id ? "0 0 18px color-mix(in srgb, var(--color-accent) 35%, transparent)" : "none",
        }}
      >
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", justifyContent: "space-between" }}>
          <div style={{ minWidth: 0, flex: "1 1 300px", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ fontWeight: 800 }}>{group.name || "Grupa tablic"}</div>
            <span style={{ fontSize: 12, opacity: 0.78 }}>Tablice: {Math.max(0, Number(group.count || 0))}</span>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {!groupPage ? (
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() => nav(`/tablica/grupa/${encodeURIComponent(group.id)}`)}
                disabled={anyBusy}
              >
                Otwórz grupę
              </button>
            ) : null}
            {canCreate ? (
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() => openCreateBoardInGroupModal(group)}
                disabled={anyBusy}
                title="Nowa tablica w tej grupie"
                aria-label="Nowa tablica w tej grupie"
                style={iconButtonStyle}
              >
                {createBusy ? "…" : "+"}
              </button>
            ) : null}
            <button
              className="btn btn-secondary"
              type="button"
              onClick={() => openRenameGroupModal(group)}
              disabled={anyBusy}
              title="Zmień nazwę grupy"
              aria-label="Zmień nazwę grupy"
              style={iconButtonStyle}
            >
              <PenIcon />
            </button>
            <button
              className="btn btn-secondary"
              type="button"
              onClick={() => openDeleteGroupModal(group)}
              disabled={anyBusy}
              title="Usuń grupę"
              aria-label="Usuń grupę"
              style={iconButtonStyle}
            >
              <TrashCanIcon />
            </button>
            <button
              className="btn btn-secondary"
              type="button"
              onClick={() => toggleGroup(group.id)}
              title={isExpanded ? "Zwiń grupę" : "Otwórz grupę"}
              aria-label={isExpanded ? "Zwiń grupę" : "Otwórz grupę"}
              style={iconButtonStyle}
            >
              <span style={{ fontSize: 20, lineHeight: 1, fontWeight: 700 }}>
                {isExpanded ? "▴" : "▾"}
              </span>
            </button>
          </div>
        </div>
        {isExpanded ? (
          groupBoardsLoading[group.id] && !groupBoardsById[group.id]?.loaded ? (
            <div role="status" style={{ marginTop: 12, fontSize: 13, opacity: 0.8 }}>Wczytuję pierwszą tablicę...</div>
          ) : (
            <BoardListSection
              title=""
              hideHeader
              items={group.items}
              currentUserId={currentUserId}
              isAdmin={isAdmin}
              onOpen={openBoard}
              openingCode={openingCode}
              onDelete={setDeleteTarget}
              onMoveGroup={openMoveBoardModal}
              onCreateGroup={openCreateGroupModal}
              editingCode={editingCode}
              editingTitle={editingTitle}
              renameSaving={renameSaving}
              onStartRename={startRenameBoard}
              onRenameChange={setEditingTitle}
              onRenameCancel={cancelRenameBoard}
              onRenameSubmit={submitRenameBoard}
              onChangeVisibility={changeBoardVisibility}
              visibilitySavingCode={visibilitySavingCode}
              hasMore={Boolean(groupBoardsById[group.id]?.hasMore)}
              loadingMore={Boolean(groupBoardsLoading[group.id])}
              onLoadMore={() => loadGroupBoards(group.id, { append: true })}
            />
          )
        ) : null}
      </div>
    );
  }

  const focusedGroupData = focusedGroupId ? groupBoardsById[focusedGroupId] : null;
  const focusedGroupSummary = focusedGroupId
    ? allGroupedBuckets.find((group) => group.id === focusedGroupId)
    : null;
  const focusedGroup = focusedGroupId
    ? {
        id: focusedGroupId,
        name: focusedGroupSummary?.name || focusedGroupData?.group?.name || "Grupa tablic",
        count: Math.max(
          Number(focusedGroupSummary?.count || 0),
          Number(focusedGroupData?.total || 0)
        ),
        visibilityType: focusedGroupSummary?.visibilityType || VISIBILITY_SHARED,
      }
    : null;

  return (
    <div style={{ display: "flex", justifyContent: "center" }}>
      <div className="glass-container" style={{ width: "100%", maxWidth: 800 }}>
        <h1 style={{ marginTop: 0 }}>{groupPage ? focusedGroup?.name || "Grupa tablic" : "Tablica interaktywna"}</h1>
        {groupPage ? (
          <>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
              <button className="btn btn-secondary" type="button" onClick={() => nav("/tablica")}>
                Wróć do lobby
              </button>
              {canCreate && focusedGroup ? (
                <button className="btn btn-primary" type="button" onClick={() => openCreateBoardInGroupModal(focusedGroup)}>
                  + Nowa tablica
                </button>
              ) : null}
            </div>
            <div className="themed-island themed-island--primary" style={{ marginBottom: 14 }}>
              {!focusedGroupId ? (
                <div>Nie wskazano grupy.</div>
              ) : groupBoardsLoading[focusedGroupId] && !focusedGroupData?.loaded ? (
                <div role="status">Wczytuję tablice grupy...</div>
              ) : focusedGroupData?.loaded ? (
                <BoardListSection
                  title={`Tablice (${focusedGroupData.total || 0})`}
                  items={focusedGroupData.items || []}
                  currentUserId={currentUserId}
                  isAdmin={isAdmin}
                  onOpen={openBoard}
                  openingCode={openingCode}
                  onDelete={setDeleteTarget}
                  onMoveGroup={openMoveBoardModal}
                  onCreateGroup={openCreateGroupModal}
                  editingCode={editingCode}
                  editingTitle={editingTitle}
                  renameSaving={renameSaving}
                  onStartRename={startRenameBoard}
                  onRenameChange={setEditingTitle}
                  onRenameCancel={cancelRenameBoard}
                  onRenameSubmit={submitRenameBoard}
                  onChangeVisibility={changeBoardVisibility}
                  visibilitySavingCode={visibilitySavingCode}
                  hasMore={Boolean(focusedGroupData.hasMore)}
                  loadingMore={Boolean(groupBoardsLoading[focusedGroupId])}
                  onLoadMore={() => loadGroupBoards(focusedGroupId, { append: true })}
                />
              ) : (
                <div>Nie udało się wczytać tej grupy.</div>
              )}
            </div>
          </>
        ) : (
          <>
        <div
          style={{
            marginTop: -4,
            marginBottom: 12,
            fontSize: 14,
            opacity: 0.9,
            lineHeight: 1.45,
          }}
        >
          W tablicach możesz dodawać zadania z platformy przyciskiem{" "}
          <b style={{ color: "var(--color-accent)" }}>Dodaj zadanie</b>{" "}
          dostępnym w szybkim panelu po boku tablicy.
        </div>

        <div className="themed-island themed-island--primary" style={{ marginBottom: 14 }}>
          <div style={{ fontWeight: 800, marginBottom: 8 }}>Status połączenia</div>
          <div style={{ fontSize: 13, opacity: 0.9 }}>
            Socket: <b>{socket.connected ? "połączony" : "łączenie..."}</b>
            {" · "}
            Auth:{" "}
            <b>
              {ping?.ok
                ? ping?.user
                  ? `zalogowany (${ping.user.email || "user"})`
                  : "niezalogowany"
                : "sprawdzam..."}
            </b>
          </div>
        </div>

        <div className="themed-island themed-island--primary" style={{ marginBottom: 14 }}>
          <div style={{ fontWeight: 800, marginBottom: 8 }}>Tablice</div>
          <input
            className="input-secondary"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Szukaj: nazwa tablicy lub grupy, kod, uczestnik..."
            aria-label="Szukaj tablic i grup"
            disabled={groupPage}
            style={{ width: "100%", boxSizing: "border-box", marginBottom: 10 }}
          />
          {hasSearchQuery ? (
            <div style={{ display: "grid", gap: 12 }}>
              {!searchQueryReady ? (
                <div style={{ fontSize: 14, opacity: 0.78 }}>Wpisz co najmniej {SEARCH_MIN_QUERY_LENGTH} znaki, aby rozpocząć wyszukiwanie.</div>
              ) : null}
              {searchQueryReady && searchLoading ? <div role="status" style={{ fontSize: 14, opacity: 0.8 }}>Szukam we wszystkich dostępnych tablicach...</div> : null}
              {searchQueryReady && searchError ? <div role="alert" style={{ color: "#ef4444", fontWeight: 700 }}>{searchError}</div> : null}
              {searchQueryReady && !searchLoading && !searchError ? (
                <>
                  <div style={{ fontSize: 13, opacity: 0.8 }}>
                    Wyniki dla: <b>{searchQuery.trim()}</b> ({searchResults.boards.length} z {searchResults.total} tablic)
                  </div>
                  {searchResults.groups.length ? (
                    <div style={{ display: "grid", gap: 8 }}>
                      <div style={{ fontWeight: 800 }}>Grupy ({searchResults.groups.length})</div>
                      {searchResults.groups.map((group) => (
                        <div
                          key={group.id}
                          className="themed-sub-island themed-island--secondary"
                          style={{ padding: "10px 12px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}
                        >
                          <div><b>{group.name}</b> <span style={{ fontSize: 12, opacity: 0.76 }}>({group.count} tablic)</span></div>
                          <button className="btn btn-secondary" type="button" onClick={() => nav(`/tablica/grupa/${encodeURIComponent(group.id)}`)}>
                            Otwórz grupę
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : null}
                  {searchResults.boards.length ? (
                    <BoardListSection
                      title={`Tablice (${searchResults.boards.length})`}
                      items={searchResults.boards}
                      currentUserId={currentUserId}
                      isAdmin={isAdmin}
                      onOpen={openBoard}
                      openingCode={openingCode}
                      onDelete={setDeleteTarget}
                      onMoveGroup={openMoveBoardModal}
                      onCreateGroup={openCreateGroupModal}
                      editingCode={editingCode}
                      editingTitle={editingTitle}
                      renameSaving={renameSaving}
                      onStartRename={startRenameBoard}
                      onRenameChange={setEditingTitle}
                      onRenameCancel={cancelRenameBoard}
                      onRenameSubmit={submitRenameBoard}
                      onChangeVisibility={changeBoardVisibility}
                      visibilitySavingCode={visibilitySavingCode}
                      renderBatchSize={SEARCH_PAGE_SIZE}
                    />
                  ) : null}
                  {searchResults.hasMore ? (
                    <button
                      className="btn btn-secondary"
                      type="button"
                      onClick={loadMoreSearchResults}
                      disabled={searchLoadingMore}
                      aria-busy={searchLoadingMore}
                      style={{ justifySelf: "center" }}
                    >
                      {searchLoadingMore
                        ? "Wczytuję..."
                        : `Pokaż kolejne (${Math.min(SEARCH_PAGE_SIZE, searchResults.total - searchResults.boards.length)})`}
                    </button>
                  ) : null}
                  {!searchResults.groups.length && !searchResults.boards.length ? (
                    <div style={{ fontSize: 14, opacity: 0.78 }}>Brak tablic ani grup pasujących do wyszukiwania.</div>
                  ) : null}
                </>
              ) : null}
            </div>
          ) : (
          <>
          {!boardsLoading && boardsLoadingMore ? (
            <div role="status" aria-live="polite" style={{ fontSize: 13, opacity: 0.78, marginBottom: 10 }}>
              Wczytuję kolejne tablice spoza grup...
            </div>
          ) : null}
          {boardsLoadError ? (
            <div className="themed-sub-island themed-island--secondary" role="alert" style={{ marginBottom: 12, padding: 12 }}>
              <div style={{ marginBottom: 8 }}>{boardsLoadError}</div>
              <button className="btn btn-secondary" type="button" onClick={loadBoards}>
                Spróbuj ponownie
              </button>
            </div>
          ) : null}
          {boardsLoading ? (
            <div style={{ minHeight: 220 }}>
              <div style={{ fontSize: 14, opacity: 0.85, marginBottom: 10 }}>Wczytuję tablice...</div>
              <BoardsLoadingPlaceholder />
            </div>
          ) : myBoards.length === 0 && groupSummaries.length === 0 && !groupsLoading ? (
            <div style={{ fontSize: 14, opacity: 0.8 }}>Na razie nie masz żadnych zapisanych tablic.</div>
          ) : (
            <>
              <CollapsibleSection
                title="Moje grupy"
                countLabel={`${groupsTotal || allGroupedBuckets.length} grup`}
                expanded={!!expandedSections.groups}
                onToggle={() => toggleSection("groups")}
                emptyText="Nie masz jeszcze żadnych grup."
              >
                {groupsLoading && allGroupedBuckets.length === 0 ? (
                  <div role="status" style={{ fontSize: 13, opacity: 0.8, marginBottom: 12 }}>Wczytuję grupy...</div>
                ) : allGroupedBuckets.length > 0 ? (
                  <div style={{ display: "grid", gap: 10, marginBottom: 12 }}>
                    {allGroupedBuckets.map((group) => renderGroupCard(group))}
                    {groupsHasMore ? (
                      <button
                        className="btn btn-secondary"
                        type="button"
                        onClick={() => loadGroupSummaries({ append: true, offset: groupSummaries.length })}
                        disabled={groupsLoading}
                        aria-busy={groupsLoading}
                        style={{ justifySelf: "center" }}
                      >
                        {groupsLoading ? "Wczytuję..." : `Pokaż kolejne grupy (${groupSummaries.length} z ${groupsTotal})`}
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </CollapsibleSection>
              <CollapsibleSection
                title="Moje tablice współdzielone"
                countLabel={boardsLoadError ? "nie wczytano" : `${boardCounts.ownShared} poza grupami`}
                expanded={!!expandedSections.ownShared}
                onToggle={() => toggleSection("ownShared")}
              >
                {ownSharedUngroupedBoards.length > 0 ? (
                  <BoardListSection
                    title="Poza grupami"
                    items={ownSharedUngroupedBoards}
                    currentUserId={currentUserId}
                    isAdmin={isAdmin}
                    onOpen={openBoard}
                    openingCode={openingCode}
                    onDelete={setDeleteTarget}
                    onMoveGroup={openMoveBoardModal}
                    onCreateGroup={openCreateGroupModal}
                    editingCode={editingCode}
                    editingTitle={editingTitle}
                    renameSaving={renameSaving}
                    onStartRename={startRenameBoard}
                    onRenameChange={setEditingTitle}
                    onRenameCancel={cancelRenameBoard}
                    onRenameSubmit={submitRenameBoard}
                    onChangeVisibility={changeBoardVisibility}
                    visibilitySavingCode={visibilitySavingCode}
                  />
                ) : null}
              </CollapsibleSection>
              <CollapsibleSection
                title="Moje tablice własne"
                countLabel={boardsLoadError ? "nie wczytano" : `${boardCounts.ownPersonal} poza grupami`}
                expanded={!!expandedSections.ownPersonal}
                onToggle={() => toggleSection("ownPersonal")}
              >
                {ownPersonalUngroupedBoards.length > 0 ? (
                  <BoardListSection
                    title="Poza grupami"
                    items={ownPersonalUngroupedBoards}
                    currentUserId={currentUserId}
                    isAdmin={isAdmin}
                    onOpen={openBoard}
                    openingCode={openingCode}
                    onDelete={setDeleteTarget}
                    onMoveGroup={openMoveBoardModal}
                    onCreateGroup={openCreateGroupModal}
                    editingCode={editingCode}
                    editingTitle={editingTitle}
                    renameSaving={renameSaving}
                    onStartRename={startRenameBoard}
                    onRenameChange={setEditingTitle}
                    onRenameCancel={cancelRenameBoard}
                    onRenameSubmit={submitRenameBoard}
                    onChangeVisibility={changeBoardVisibility}
                    visibilitySavingCode={visibilitySavingCode}
                  />
                ) : null}
              </CollapsibleSection>
              <CollapsibleSection
                title="Udostępnione mi"
                countLabel={boardsLoadError ? "nie wczytano" : `${boardCounts.sharedWithMe} poza grupami`}
                expanded={!!expandedSections.sharedWithMe}
                onToggle={() => toggleSection("sharedWithMe")}
              >
                {sharedWithMeUngroupedBoards.length > 0 ? (
                  <BoardListSection
                    title="Poza grupami"
                    items={sharedWithMeUngroupedBoards}
                    currentUserId={currentUserId}
                    isAdmin={isAdmin}
                    onOpen={openBoard}
                    openingCode={openingCode}
                    onDelete={setDeleteTarget}
                    onMoveGroup={openMoveBoardModal}
                    onCreateGroup={openCreateGroupModal}
                    editingCode={editingCode}
                    editingTitle={editingTitle}
                    renameSaving={renameSaving}
                    onStartRename={startRenameBoard}
                    onRenameChange={setEditingTitle}
                    onRenameCancel={cancelRenameBoard}
                    onRenameSubmit={submitRenameBoard}
                    onChangeVisibility={changeBoardVisibility}
                    visibilitySavingCode={visibilitySavingCode}
                  />
                ) : null}
              </CollapsibleSection>
              {boardsHasMore ? (
                <button
                  className="btn btn-secondary"
                  type="button"
                  onClick={loadMoreBoards}
                  disabled={boardsLoadingMore}
                  aria-busy={boardsLoadingMore}
                  style={{ display: "flex", margin: "14px auto 0" }}
                >
                  {boardsLoadingMore ? "Wczytuję..." : `Pokaż kolejne tablice spoza grup (${myBoards.length} z ${boardsTotal})`}
                </button>
              ) : null}
            </>
          )}
          </>
          )}
        </div>

        <div className="themed-island themed-island--primary" style={{ marginBottom: 14 }}>
          <div style={{ fontWeight: 800, marginBottom: 8 }}>Dołącz linkiem lub kodem</div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <input
              className="input-secondary"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Kod tablicy, np. A7K9Q2"
              style={{ flex: "1 1 240px", minWidth: 220 }}
            />
            <button
              className="btn btn-secondary"
              type="button"
              onClick={onJoin}
              disabled={joinBusy}
              aria-busy={joinBusy}
            >
              {joinBusy ? <span className="button-busy-spinner" aria-hidden="true" /> : null}
              {joinBusy ? "Dołączam..." : "Dołącz"}
            </button>
          </div>
        </div>

        <div className="themed-island themed-island--primary" style={{ marginTop: 16 }}>
          {canCreate ? (
            <button
              className="btn btn-primary"
              type="button"
              onClick={() => {
                setCreateBoardVisibilityType(VISIBILITY_PERSONAL);
                setTitleTouched(false);
                setTitle(computeNextDefaultBoardTitle(myBoards, currentUserId, VISIBILITY_PERSONAL));
                setCreateModalOpen(true);
              }}
            >
              Utwórz tablicę
            </button>
          ) : (
            <div style={{ fontSize: 14, opacity: 0.85 }}>Aby utworzyć tablicę, zaloguj się.</div>
          )}
        </div>
          </>
        )}

        {err ? <div style={{ marginTop: 14, color: "var(--color-accent)" }}>{err}</div> : null}

        <MatematronModal
          open={createModalOpen}
          variant="info"
          title="Utwórz tablicę"
          primaryText={createBusy ? "Tworzenie..." : "Utwórz"}
          onPrimary={onCreate}
          primaryDisabled={createBusy}
          secondaryText="Anuluj"
          secondaryDisabled={createBusy}
          onSecondary={() => {
            if (createBusy) return;
            setCreateModalOpen(false);
            setCreateBoardVisibilityType(VISIBILITY_PERSONAL);
          }}
          onClose={() => {
            if (createBusy) return;
            setCreateModalOpen(false);
            setCreateBoardVisibilityType(VISIBILITY_PERSONAL);
          }}
          showBadge
          badgeText="Tablica"
        >
          <div style={{ display: "grid", gap: 10 }}>
            <div>
              <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 6 }}>Tytuł</div>
              <input
                className="input-secondary"
                value={title}
                onChange={(e) => {
                  setTitleTouched(true);
                  setTitle(e.target.value);
                }}
                placeholder={nextDefaultTitle}
              />
            </div>
            <div>
              <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 6 }}>Typ tablicy</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  className={normalizeVisibilityType(createBoardVisibilityType) === VISIBILITY_PERSONAL ? "btn btn-primary" : "btn btn-secondary"}
                  type="button"
                  onClick={() => setCreateBoardVisibilityType(VISIBILITY_PERSONAL)}
                >
                  Własna (prywatna)
                </button>
                <button
                  className={normalizeVisibilityType(createBoardVisibilityType) === VISIBILITY_SHARED ? "btn btn-primary" : "btn btn-secondary"}
                  type="button"
                  onClick={() => setCreateBoardVisibilityType(VISIBILITY_SHARED)}
                >
                  Współdzielona
                </button>
              </div>
            </div>
            <div style={{ fontSize: 12, opacity: 0.78 }}>
              Limit uczestników dla tablicy współdzielonej: <b>{FIXED_MAX_PARTICIPANTS}</b>
            </div>
          </div>
        </MatematronModal>

        <MatematronModal
          open={!!deleteTarget}
          variant="info"
          title="Potwierdź usunięcie"
          message={
            deleteTarget
              ? `Czy na pewno chcesz usunąć tablicę "${deleteTarget.title || deleteTarget.code}"? Tej operacji nie da się cofnąć z poziomu zwykłego widoku.`
              : ""
          }
          primaryText="Usuń tablicę"
          onPrimary={confirmDeleteBoard}
          secondaryText="Anuluj"
          onSecondary={() => setDeleteTarget(null)}
          onClose={() => setDeleteTarget(null)}
          showBadge
          badgeText="Tablica"
        />

        <MatematronModal
          open={!!createGroupTarget}
          variant="info"
          title="Utwórz grupę"
          primaryText={groupActionBusyKey.startsWith("create_group:") ? "Tworzenie..." : "Utwórz"}
          onPrimary={createGroupFromBoard}
          primaryDisabled={groupActionBusyKey.startsWith("create_group:")}
          secondaryText="Anuluj"
          secondaryDisabled={groupActionBusyKey.startsWith("create_group:")}
          onSecondary={() => {
            if (groupActionBusyKey.startsWith("create_group:")) return;
            setCreateGroupTarget(null);
            setCreateGroupName("");
          }}
          onClose={() => {
            if (groupActionBusyKey.startsWith("create_group:")) return;
            setCreateGroupTarget(null);
            setCreateGroupName("");
          }}
          showBadge
          badgeText="Grupa"
        >
          <div style={{ display: "grid", gap: 10 }}>
            <div style={{ fontSize: 14, opacity: 0.9 }}>
              Utworzyć nową grupę na bazie tablicy <b>{createGroupTarget?.title || createGroupTarget?.code || "-"}</b>?
            </div>
            <div>
              <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 6 }}>Nazwa grupy</div>
              <input
                className="input-secondary"
                value={createGroupName}
                onChange={(e) => setCreateGroupName(e.target.value)}
                placeholder="Nazwa grupy"
              />
            </div>
          </div>
        </MatematronModal>

        <MatematronModal
          open={!!createBoardGroup}
          variant="info"
          title="Nowa tablica w grupie"
          primaryText={groupActionBusyKey.startsWith("create_board:") ? "Tworzenie..." : "Utwórz"}
          onPrimary={createBoardInGroup}
          primaryDisabled={groupActionBusyKey.startsWith("create_board:")}
          secondaryText="Anuluj"
          secondaryDisabled={groupActionBusyKey.startsWith("create_board:")}
          onSecondary={() => {
            if (groupActionBusyKey.startsWith("create_board:")) return;
            setCreateBoardGroup(null);
            setCreateBoardTitle("");
            setCreateBoardVisibilityType(VISIBILITY_SHARED);
          }}
          onClose={() => {
            if (groupActionBusyKey.startsWith("create_board:")) return;
            setCreateBoardGroup(null);
            setCreateBoardTitle("");
            setCreateBoardVisibilityType(VISIBILITY_SHARED);
          }}
          showBadge
          badgeText="Tablica"
        >
          <div style={{ display: "grid", gap: 10 }}>
            <div style={{ fontSize: 14, opacity: 0.9 }}>
              Utworzyć tablicę w grupie <b>{createBoardGroup?.name || "Grupa tablic"}</b>?
            </div>
            <div>
              <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 6 }}>Nazwa tablicy</div>
              <input
                className="input-secondary"
                value={createBoardTitle}
                onChange={(e) => setCreateBoardTitle(e.target.value)}
                placeholder="Nazwa tablicy"
              />
            </div>
            <div>
              <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 6 }}>Typ tablicy</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  className={normalizeVisibilityType(createBoardVisibilityType) === VISIBILITY_SHARED ? "btn btn-primary" : "btn btn-secondary"}
                  type="button"
                  onClick={() => setCreateBoardVisibilityType(VISIBILITY_SHARED)}
                >
                  Współdzielona
                </button>
                <button
                  className={normalizeVisibilityType(createBoardVisibilityType) === VISIBILITY_PERSONAL ? "btn btn-primary" : "btn btn-secondary"}
                  type="button"
                  onClick={() => setCreateBoardVisibilityType(VISIBILITY_PERSONAL)}
                >
                  Własna (prywatna)
                </button>
              </div>
            </div>
            <div style={{ fontSize: 12, opacity: 0.78 }}>
              Limit uczestników: <b>{FIXED_MAX_PARTICIPANTS}</b>
            </div>
          </div>
        </MatematronModal>

        <MatematronModal
          open={!!moveTargetBoard}
          variant="info"
          title="Przenieś tablicę"
          primaryText={moveBoardBusy ? "Przenoszę..." : "Przenieś"}
          onPrimary={moveBoardBusy ? undefined : confirmMoveBoardToGroup}
          secondaryText="Anuluj"
          onSecondary={() => {
            if (moveBoardBusy) return;
            setMoveTargetBoard(null);
            setMoveGroupId("");
          }}
          onClose={() => {
            if (moveBoardBusy) return;
            setMoveTargetBoard(null);
            setMoveGroupId("");
          }}
          showBadge
          badgeText="Grupy"
        >
          <div style={{ display: "grid", gap: 10 }}>
            <div style={{ fontSize: 14, opacity: 0.9 }}>
              Wybierz docelową grupę dla tablicy <b>{moveTargetBoard?.title || moveTargetBoard?.code || "-"}</b>.
            </div>
            <div>
              <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 6 }}>Grupa docelowa</div>
              <select
                className="input-secondary"
                value={moveGroupId}
                disabled={moveBoardBusy}
                onChange={(e) => setMoveGroupId(e.target.value)}
                style={{ width: "100%" }}
              >
                <option value="">Poza grupami</option>
                  {allGroupedBuckets.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name || "Grupa tablic"} ({Math.max(0, Number(group.count || 0))})
                    </option>
                  ))}
              </select>
            </div>
          </div>
        </MatematronModal>

        <MatematronModal
          open={!!renameGroupTarget}
          variant="info"
          title="Zmień nazwę grupy"
          primaryText={groupActionBusyKey === `rename_group:${String(renameGroupTarget?.id || "")}` ? "Zapisywanie..." : "Zapisz"}
          onPrimary={
            groupActionBusyKey === `rename_group:${String(renameGroupTarget?.id || "")}`
              ? undefined
              : confirmRenameGroup
          }
          secondaryText="Anuluj"
          onSecondary={() => {
            setRenameGroupTarget(null);
            setRenameGroupName("");
          }}
          onClose={() => {
            setRenameGroupTarget(null);
            setRenameGroupName("");
          }}
          showBadge
          badgeText="Grupy"
        >
          <div style={{ display: "grid", gap: 10 }}>
            <div style={{ fontSize: 14, opacity: 0.9 }}>
              Zmień nazwę grupy <b>{renameGroupTarget?.name || "Grupa tablic"}</b>.
            </div>
            <div>
              <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 6 }}>Nowa nazwa</div>
              <input
                className="input-secondary"
                value={renameGroupName}
                onChange={(e) => setRenameGroupName(e.target.value)}
                placeholder="Nazwa grupy"
              />
            </div>
          </div>
        </MatematronModal>

        <MatematronModal
          open={!!deleteGroupTarget}
          variant="info"
          title="Usuń grupę"
          primaryText={groupActionBusyKey === `delete_group:${String(deleteGroupTarget?.id || "")}` ? "Usuwanie..." : "Usuń grupę"}
          onPrimary={
            groupActionBusyKey === `delete_group:${String(deleteGroupTarget?.id || "")}`
              ? undefined
              : confirmDeleteGroup
          }
          secondaryText="Anuluj"
          onSecondary={() => setDeleteGroupTarget(null)}
          onClose={() => setDeleteGroupTarget(null)}
          showBadge
          badgeText="Grupy"
        >
          <div style={{ fontSize: 14, opacity: 0.9 }}>
            Usunąć grupę <b>{deleteGroupTarget?.name || "Grupa tablic"}</b>? Tablice zostaną przeniesione do sekcji
            <b> „Poza grupami”</b>.
          </div>
        </MatematronModal>
      </div>
    </div>
  );
}
